import { useState, useEffect, useRef, type DragEvent, type ChangeEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getListingsByUser,
  getPurchasesByUser,
  getFilesByDeals,
  uploadFile,
  deleteFile,
  getDownloadUrl,
  toClientDeals,
} from '../lib/directus';
import type { ClientDeal, ClientFile } from '../lib/directus';
import ClientLayout from '../components/ClientLayout';

const FILE_CATEGORIES = [
  { id: 'all', name: 'All Documents', color: 'bg-gray-500' },
  { id: 'contracts', name: 'Contracts', color: 'bg-[#134052]' },
  { id: 'disclosures', name: 'Disclosures', color: 'bg-[#C5A880]' },
  { id: 'inspections', name: 'Inspections', color: 'bg-blue-600' },
  { id: 'photos', name: 'Photos', color: 'bg-green-600' },
  { id: 'other', name: 'Other', color: 'bg-gray-600' },
] as const;

export default function DocumentsPage() {
  const { user, token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState<ClientDeal[]>([]);
  const [files, setFiles] = useState<ClientFile[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ id: string; name: string; progress: number }[]>([]);

  useEffect(() => {
    async function loadDocuments() {
      if (!user || !token) return;

      try {
        const [listings, purchases] = await Promise.all([
          getListingsByUser(user.id, token),
          getPurchasesByUser(user.id, token),
        ]);

        const clientDeals = toClientDeals(listings, purchases);
        setDeals(clientDeals);

        if (clientDeals.length > 0) {
          const dealIds = clientDeals.map((d) => d.id);
          const filesData = await getFilesByDeals(dealIds, token);
          setFiles(filesData);
        }
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        setLoading(false);
      }
    }

    void loadDocuments();
  }, [user, token]);

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    await handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      await handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = async (filesToUpload: File[]) => {
    if (!filesToUpload.length || !token) return;

    const activeDeal = deals.find((d) => d.status !== 'closed' && d.status !== 'terminated') ?? deals[0];
    if (!activeDeal) {
      alert('No active deal found. Please contact your agent.');
      return;
    }

    for (const file of filesToUpload) {
      const fileId = `${Date.now()}-${file.name}`;
      setUploadingFiles((prev) => [...prev, { id: fileId, name: file.name, progress: 0 }]);

      try {
        const uploadedFile = await uploadFile(
          file,
          {
            category: selectedCategory === 'all' ? 'other' : selectedCategory,
            deal_id: activeDeal.id,
          },
          token
        );

        setUploadingFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, progress: 100 } : f)));
        setFiles((prev) => [...prev, uploadedFile]);

        setTimeout(() => {
          setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
        }, 1000);
      } catch (error) {
        console.error('Error uploading file:', error);
        alert(`Failed to upload ${file.name}`);
        setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
      }
    }
  };

  const handleDownload = async (file: ClientFile) => {
    if (!token) return;

    try {
      const response = await fetch(getDownloadUrl(file.id), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename_download ?? file.title ?? 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleDelete = async (fileId: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    if (!token) return;

    try {
      await deleteFile(fileId, token);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (filename?: string) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext ?? '')) return '🖼️';
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext ?? '')) return '📝';
    if (['xls', 'xlsx'].includes(ext ?? '')) return '📊';
    return '📎';
  };

  const filteredFiles = selectedCategory === 'all' ? files : files.filter((f) => f.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#DDD6CE]">
        <div className="text-[#134052] text-xl font-semibold">Loading documents...</div>
      </div>
    );
  }

  return (
    <ClientLayout user={user}>
      {/* Category filters */}
      <div className="mb-6 flex flex-wrap gap-2">
        {FILE_CATEGORIES.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedCategory === category.id
                ? `${category.color} text-white shadow-lg`
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {category.name}
            <span className="ml-2 text-xs opacity-75">
              ({category.id === 'all' ? files.length : files.filter((f) => f.category === category.id).length})
            </span>
          </button>
        ))}
      </div>

      {/* Upload zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`mb-8 border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
          isDragging ? 'border-[#134052] bg-[#134052]/5 scale-105' : 'border-gray-300 hover:border-[#C5A880] bg-white'
        }`}
      >
        <div className="text-5xl mb-4">☁️</div>
        <h3 className="text-lg font-semibold text-[#134052] mb-2">
          {isDragging ? 'Drop files here' : 'Upload Documents'}
        </h3>
        <p className="text-gray-600 mb-4">Drag and drop files here, or click to browse</p>
        <p className="text-sm text-gray-500">
          Files will be added to: {FILE_CATEGORIES.find((c) => c.id === selectedCategory)?.name}
        </p>
        <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" />
      </div>

      {/* Upload progress */}
      {uploadingFiles.length > 0 && (
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-[#134052] mb-4">Uploading Files</h3>
          <div className="space-y-4">
            {uploadingFiles.map((file) => (
              <div key={file.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{file.name}</span>
                  <span className="text-xs text-gray-500">{file.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#C5A880] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Files list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-[#134052] to-[#1a5368] px-6 py-4">
          <h3 className="text-xl font-bold text-white">
            {FILE_CATEGORIES.find((c) => c.id === selectedCategory)?.name}
          </h3>
        </div>
        <div className="p-6">
          {filteredFiles.length > 0 ? (
            <div className="space-y-3">
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="text-3xl">{getFileIcon(file.filename_download)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#134052] truncate">
                        {file.title ?? file.filename_download ?? 'Untitled'}
                      </p>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-xs text-gray-500">{formatFileSize(file.filesize)}</span>
                        {file.uploaded_on && (
                          <span className="text-xs text-gray-500">
                            {new Date(file.uploaded_on).toLocaleDateString()}
                          </span>
                        )}
                        {file.category && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              FILE_CATEGORIES.find((c) => c.id === file.category)?.color ?? 'bg-gray-500'
                            } text-white`}
                          >
                            {FILE_CATEGORIES.find((c) => c.id === file.category)?.name ?? file.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleDownload(file)}
                      className="p-2 text-[#134052] hover:bg-[#134052] hover:text-white rounded-lg transition-colors"
                      title="Download"
                    >
                      ⬇
                    </button>
                    <button
                      onClick={() => handleDelete(file.id)}
                      className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors"
                      title="Delete"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📁</div>
              <p className="text-gray-500">
                {selectedCategory === 'all'
                  ? 'No documents yet. Upload your first document above!'
                  : `No ${FILE_CATEGORIES.find((c) => c.id === selectedCategory)?.name.toLowerCase()} yet.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
}
